/**
 * 右滑返回手势的 DOM 接线：Pointer Events → swipeBack 状态机 → 跟手 transform。
 * commit 统一调用 onBack（= router.back()），与返回键/应用内按钮同一条出栈路径。
 *
 * 要求宿主元素带 touch-action: pan-y（shell.module.scss 的 .swipeArea）：
 * 垂直滚动交还浏览器，水平移动才会持续派发 pointermove 给我们。
 */
import { useEffect, useRef } from 'preact/hooks';
import type { RefObject } from 'preact';
import { createSwipeBackGesture, type SwipePoint } from './swipeBack';

const point = (e: PointerEvent): SwipePoint => ({
  pointerId: e.pointerId,
  x: e.clientX,
  y: e.clientY,
  timeStamp: e.timeStamp,
});

export function useSwipeBack(enabled: boolean, onBack: () => void): RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null);
  const onBackRef = useRef(onBack);
  useEffect(() => {
    onBackRef.current = onBack;
  });

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;

    // 跟手拖动结束后浏览器仍会派发 click（pointer capture 下目标是本元素或其子级），
    // 不拦会误触松手位置下的按钮——尤其 commit 后已换页，点到的是新页面的元素。
    let suppressClick = false;
    let suppressTimer: ReturnType<typeof setTimeout> | undefined;

    const gesture = createSwipeBackGesture({
      canStart: () => true, // enabled 变化走 effect 重建，这里恒 true
      setOffset(px) {
        el.style.transition = '';
        el.style.transform = px > 0 ? `translateX(${px}px)` : '';
      },
      end(committed) {
        suppressClick = true;
        clearTimeout(suppressTimer);
        // click 在 pointerup 同一任务内派发，宏任务兜底复位（有些情况不派发 click）
        suppressTimer = setTimeout(() => {
          suppressClick = false;
        }, 0);
        if (committed) {
          el.style.transition = '';
          el.style.transform = '';
          onBackRef.current();
        } else {
          el.style.transition = 'transform 0.15s ease-out';
          el.style.transform = '';
        }
      },
    });

    const onDown = (e: PointerEvent): void => gesture.down(point(e), el.clientWidth);
    const onMove = (e: PointerEvent): void => {
      if (gesture.move(point(e))) {
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          // 不支持 capture 时靠冒泡也能收到 move，只是手指移出元素会丢
        }
      }
    };
    const onUp = (e: PointerEvent): void => gesture.up(point(e));
    const onCancel = (e: PointerEvent): void => gesture.cancel(e.pointerId);
    const onClickCapture = (e: MouseEvent): void => {
      if (suppressClick) {
        suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
      }
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onCancel);
    el.addEventListener('click', onClickCapture, { capture: true });
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onCancel);
      el.removeEventListener('click', onClickCapture, { capture: true });
      clearTimeout(suppressTimer);
      el.style.transition = '';
      el.style.transform = '';
    };
  }, [enabled]);

  return ref;
}
